import { z } from "zod";
import type { UserId } from "@praesto/shared-types";

export const AiQueryInputSchema = z.object({
  /** Free-text user question */
  query: z.string().min(1).max(10_000),
  /** Optional existing session ID (Memory Bank) to continue a conversation */
  sessionId: z.string().optional(),
});

export type AiQueryInput = z.infer<typeof AiQueryInputSchema>;

export interface AiQueryResult {
  /** Full assistant response text (synthesized after streaming completes) */
  readonly text: string;
  /** Source citations extracted from tool calls */
  readonly citations: ReadonlyArray<{
    readonly sopId: string;
    readonly sopTitle: string;
    readonly sectionRef: string;
  }>;
  /** Session ID — pass back on the next call to continue the conversation */
  readonly sessionId: string;
  /** Token usage (best-effort, from the underlying model) */
  readonly tokens: {
    readonly input: number;
    readonly output: number;
  };
  /** Total latency in milliseconds */
  readonly latencyMs: number;
}

export interface AiStreamChunk {
  /**
   * `session` is emitted ONCE at the start of a stream carrying the engine
   * session id in `content`. The client captures it and passes it back as
   * `AiQueryInput.sessionId` on subsequent turns so the whole conversation
   * accumulates in ONE engine session (and shows up in the session history).
   *
   * `error` is emitted by the proxy when the turn failed (agent threw, or ended
   * without any text); `content` is a user-facing message. Nothing follows it.
   */
  readonly type: "text" | "tool_call" | "tool_result" | "session" | "done" | "error";
  readonly content: string;
  readonly metadata?: Record<string, unknown>;
}

/** One past conversation, as listed in the assistant's history. */
export interface AiSessionSummary {
  readonly id: string;
  /** Epoch seconds of the last activity (engine `lastUpdateTime`). */
  readonly lastUpdateTime: number;
  /** First user message, used as the list label (best-effort). */
  readonly preview?: string;
}

/** A single message inside a recalled session transcript. */
export interface AiSessionMessage {
  readonly role: "user" | "assistant";
  readonly content: string;
  readonly citations?: ReadonlyArray<{
    readonly sopId: string;
    readonly sopTitle: string;
    readonly sectionRef: string;
  }>;
}

/** Full transcript of a past session, used to reopen it in the chat. */
export interface AiSessionTranscript {
  readonly id: string;
  readonly messages: ReadonlyArray<AiSessionMessage>;
}

/** Caller identity (extracted from JWT by the proxy Cloud Function) */
export interface AiCallerContext {
  readonly tenantId: string;
  readonly userId: UserId;
  readonly userEmail: string;
}
